import React, { useState, useEffect, useMemo } from 'react';
import { X, Calendar, MapPin, Download, Eye, ChevronUp, ChevronDown } from 'lucide-react';
import { checklistCompletionService, ChecklistCompletion } from '@/firebase/checklistCompletionService';
import { locationService, Location } from '@/firebase/locationService';
import { assetService } from '@/firebase/assetService';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { Checklist, checklistService } from '@/firebase/checklistService';
import { formatTimeDisplay } from '@/utils/checklistTimeTracker';
import { fetchUserRoles, getUserSystemRoleForOrganization, isSystemUserInOrganization } from '@/utils/roleHelpers';

interface ChecklistHistoryProps {
  checklist: Checklist;
  onClose: () => void;
}

const ChecklistHistory: React.FC<ChecklistHistoryProps> = ({ checklist, onClose }) => {
  const { currentOrganization } = useOrganization();
  const { user } = useAuth();
  const [completions, setCompletions] = useState<ChecklistCompletion[]>([]);
  const [locations, setLocations] = useState<Record<string, Location>>({});
  const [assetNames, setAssetNames] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [userLocationIds, setUserLocationIds] = useState<string[]>([]);
  const [isSystemUser, setIsSystemUser] = useState<boolean>(false);
  
  // Initialize with empty date range to show all completions by default
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const [selectedCompletion, setSelectedCompletion] = useState<ChecklistCompletion | null>(null);
  const [viewingChecklist, setViewingChecklist] = useState<Checklist | null>(null);
  const [isLoadingCompletionDetails, setIsLoadingCompletionDetails] = useState(false);
  const [sortField, setSortField] = useState<string>('completedAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    loadUserLocations();
  }, [user?.id, currentOrganization?.id]);

  useEffect(() => {
    loadCompletions();
    loadLocations();
  }, [checklist.id, currentOrganization?.id, userLocationIds.length, isSystemUser]);

  const loadUserLocations = async () => {
    if (user?.id && currentOrganization?.id) {
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('@/firebase/config');
        const userRef = doc(db, 'users', user.id);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data();
          const locationIds = userData.locationIds || [];
          setUserLocationIds(locationIds);
          
          // Use organization-specific role check
          if (currentOrganization?.id) {
            setIsSystemUser(isSystemUserInOrganization(userData, currentOrganization.id));
          } else {
            // Fallback: check if user is a system user using legacy method
            const roles = await fetchUserRoles(user.id);
            setIsSystemUser(roles.isSystemUser);
          }
        }
      } catch (error) {
        console.error('Error loading user locations:', error);
        setUserLocationIds([]);
      }
    } else {
      setUserLocationIds([]);
    }
  };

  const loadLocations = async () => {
    if (!currentOrganization?.id) return;
    
    try {
      // For system users, only load accessible locations
      let userRole: string | undefined;
      if (user?.id) {
        try {
          const { doc, getDoc } = await import('firebase/firestore');
          const { db } = await import('@/firebase/config');
          const userRef = doc(db, 'users', user.id);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            const userData = userSnap.data();
            userRole = userData.role;
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      }
      
      const result = isSystemUser && userLocationIds.length > 0
        ? await locationService.getAccessibleLocations(currentOrganization.id, user?.id, userRole, userLocationIds)
        : await locationService.getAllLocations(currentOrganization.id);
      
      if (result.success && result.data) {
        const locationsMap: Record<string, Location> = {};
        result.data.forEach((loc: Location) => {
          if (loc.id) {
            locationsMap[loc.id] = loc;
          }
        });
        setLocations(locationsMap);
      }
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  const loadCompletions = async () => {
    if (!checklist.id) return;
    
    setIsLoading(true);
    try {
      const result = await checklistCompletionService.getCompletionsForChecklist(checklist.id);
      if (result.success && result.data) {
        let allCompletions = result.data;
        
        // Filter completions by accessible locations for system users
        if (isSystemUser && userLocationIds.length > 0) {
          allCompletions = allCompletions.filter(completion => 
            completion.locationId && userLocationIds.includes(completion.locationId)
          );
        }
        
        setCompletions(allCompletions);
        
        // Fetch asset names for completions that have assetId but no assetName (use filtered completions)
        const assetIdsToFetch = new Set<string>();
        allCompletions.forEach(completion => {
          // Check both direct assetId and assetId in variableValues
          const assetId = completion.assetId || completion.variableValues?.assetId;
          if (assetId && !completion.assetName) {
            assetIdsToFetch.add(assetId);
          }
        });
        
        // Fetch asset names in parallel
        if (assetIdsToFetch.size > 0) {
          const assetNamePromises = Array.from(assetIdsToFetch).map(async (assetId) => {
            try {
              const assetResult = await assetService.getAsset(assetId);
              if (assetResult.success && assetResult.data) {
                return { assetId, assetName: assetResult.data.assetName || '' };
              }
            } catch (error) {
              console.error(`Error fetching asset ${assetId}:`, error);
            }
            return null;
          });
          
          const assetNameResults = await Promise.all(assetNamePromises);
          const assetNamesMap: Record<string, string> = {};
          assetNameResults.forEach(result => {
            if (result) {
              assetNamesMap[result.assetId] = result.assetName;
            }
          });
          
          setAssetNames(assetNamesMap);
        }
      }
    } catch (error) {
      console.error('Error loading completions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to convert Firestore timestamp to Date
  const getDateFromValue = (value: any): Date | null => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (value?.toDate && typeof value.toDate === 'function') return value.toDate();
    if (typeof value === 'number') return new Date(value);
    if (typeof value === 'string') return new Date(value);
    return null;
  };

  // Filter and sort completions based on date range, location, and sort settings
  const filteredCompletions = useMemo(() => {
    const filtered = completions.filter(completion => {
      // Filter by location dropdown selection
      if (selectedLocationId && completion.locationId !== selectedLocationId) {
        return false;
      }

      // For system users, ensure only accessible locations are shown even when no location is selected
      if (isSystemUser && userLocationIds.length > 0) {
        if (!completion.locationId || !userLocationIds.includes(completion.locationId)) {
          return false;
        }
      }

      // Filter by date range
      const completionDate = getDateFromValue(completion.completedAt);
      if (!completionDate) return false;

      if (startDate) {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        if (completionDate < start) return false;
      }

      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        if (completionDate > end) return false;
      }

      return true;
    });

    // Sort the filtered completions
    const sorted = [...filtered].sort((a, b) => {
      if (sortField === 'assetName') {
        // Get asset names for comparison
        const assetIdA = a.assetId || a.variableValues?.assetId;
        const assetIdB = b.assetId || b.variableValues?.assetId;
        const assetNameA = a.assetName || (assetIdA ? assetNames[assetIdA] : '') || '';
        const assetNameB = b.assetName || (assetIdB ? assetNames[assetIdB] : '') || '';
        
        const comparison = assetNameA.localeCompare(assetNameB, undefined, { sensitivity: 'base', numeric: true });
        return sortDirection === 'asc' ? comparison : -comparison;
      } else if (sortField === 'completedAt') {
        const dateA = getDateFromValue(a.completedAt);
        const dateB = getDateFromValue(b.completedAt);
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        const comparison = dateA.getTime() - dateB.getTime();
        return sortDirection === 'asc' ? comparison : -comparison;
      }
      return 0;
    });

    return sorted;
  }, [completions, startDate, endDate, selectedLocationId, isSystemUser, userLocationIds, sortField, sortDirection, assetNames]);

  // Get unique locations from completions with their names
  const availableLocations = useMemo(() => {
    const locationMap = new Map<string, { id: string; name: string }>();
    completions.forEach(completion => {
      if (completion.locationId) {
        // For system users, only include locations they have access to
        if (isSystemUser && userLocationIds.length > 0) {
          if (!userLocationIds.includes(completion.locationId)) {
            return; // Skip this location
          }
        }
        
        // Check if we already have this location
        if (!locationMap.has(completion.locationId)) {
          const location = locations[completion.locationId];
          const locationName = location?.name || completion.locationName || completion.locationId;
          locationMap.set(completion.locationId, {
            id: completion.locationId,
            name: locationName,
          });
        }
      }
    });
    // Sort by name for better UX
    return Array.from(locationMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [completions, locations, isSystemUser, userLocationIds]);

  // Auto-select location filter if user has access to only one location
  useEffect(() => {
    if (availableLocations.length === 1 && !selectedLocationId && availableLocations[0]?.id) {
      setSelectedLocationId(availableLocations[0].id);
    }
  }, [availableLocations.length, selectedLocationId, availableLocations]);

  const formatDate = (date: Date | null): string => {
    if (!date) return 'N/A';
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleViewPDF = (completion: ChecklistCompletion) => {
    if (completion.pdfUrl) {
      window.open(completion.pdfUrl, '_blank');
    }
  };

  const handleDownloadPDF = (completion: ChecklistCompletion) => {
    if (completion.pdfUrl) {
      const link = document.createElement('a');
      link.href = completion.pdfUrl;
      link.download = `${checklist.name || 'checklist'}_${completion.id || 'completion'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const getCompletionStatus = (completion: ChecklistCompletion): { completed: number; total: number } => {
    const items = completion.items || {};
    const total = Object.keys(items).length;
    const completed = Object.values(items).filter(v => v === true).length;
    return { completed, total };
  };

  const handleViewCompletion = async (completion: ChecklistCompletion) => {
    setSelectedCompletion(completion);
    setIsLoadingCompletionDetails(true);

    try {
      const checklistResult = await checklistService.getChecklist(completion.checklistId);
      if (checklistResult.success && checklistResult.data) {
        setViewingChecklist(checklistResult.data);
      } else {
        console.error('Failed to load checklist:', checklistResult.error);
        // Still show the completion even if checklist template fails to load
      }
    } catch (error: any) {
      console.error('Error loading completion details:', error);
    } finally {
      setIsLoadingCompletionDetails(false);
    }
  };

  const handleCloseCompletionDetails = () => {
    setSelectedCompletion(null);
    setViewingChecklist(null);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Checklist History</h2>
            <p className="text-sm text-gray-500 mt-1">{checklist.name}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Filters */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Start Date Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            {/* End Date Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            {/* Location Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <MapPin className="w-4 h-4 inline mr-1" />
                Location
              </label>
              <select
                value={selectedLocationId}
                onChange={(e) => setSelectedLocationId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="">All Locations</option>
                {availableLocations.map(location => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Filter Status and Clear Button */}
          <div className="mt-3 flex items-center justify-between">
            {(startDate || endDate || selectedLocationId) && (
              <div className="text-xs text-gray-600">
                Filters active: 
                {startDate && <span className="ml-1">From {startDate}</span>}
                {endDate && <span className="ml-1">To {endDate}</span>}
                {selectedLocationId && <span className="ml-1">Location filtered</span>}
              </div>
            )}
            {(startDate || endDate || selectedLocationId) && (
              <button
                onClick={() => {
                  setStartDate('');
                  setEndDate('');
                  setSelectedLocationId('');
                }}
                className="text-sm text-purple-600 hover:text-purple-800 font-medium"
              >
                Clear All Filters
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-gray-500">Loading history...</div>
            </div>
          ) : filteredCompletions.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-gray-500 text-center">
                <p className="font-medium">No completions found</p>
                <p className="text-sm mt-1">
                  {completions.length === 0
                    ? 'This checklist has not been completed yet.'
                    : 'No completions match the selected filters.'}
                </p>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full table-auto border-collapse">
                <thead className="bg-gray-50">
                  <tr>
                    <th 
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200 cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('completedAt')}
                    >
                      <div className="flex items-center gap-1">
                        Completed At
                        {sortField === 'completedAt' && (
                          sortDirection === 'asc' ? (
                            <ChevronUp className="w-3 h-3" />
                          ) : (
                            <ChevronDown className="w-3 h-3" />
                          )
                        )}
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200">
                      Location
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200">
                      Completed By
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200">
                      Status
                    </th>
                    <th 
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border border-gray-200 cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('assetName')}
                    >
                      <div className="flex items-center gap-1">
                        Asset Name
                        {sortField === 'assetName' && (
                          sortDirection === 'asc' ? (
                            <ChevronUp className="w-3 h-3" />
                          ) : (
                            <ChevronDown className="w-3 h-3" />
                          )
                        )}
                      </div>
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 border border-gray-200">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCompletions.map((completion, index) => {
                    const completionDate = getDateFromValue(completion.completedAt);
                    const status = getCompletionStatus(completion);
                    const location = completion.locationId ? locations[completion.locationId] : null;
                    
                    // Get asset name from completion or fetched assetNames map
                    const assetId = completion.assetId || completion.variableValues?.assetId;
                    const assetName = completion.assetName || (assetId ? assetNames[assetId] : '');

                    return (
                      <tr
                        key={completion.id || index}
                        className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100 cursor-pointer`}
                        onClick={() => handleViewCompletion(completion)}
                      >
                        <td className="px-4 py-3 border border-gray-200">
                          <div className="text-sm text-gray-900">
                            {formatDate(completionDate)}
                          </div>
                        </td>
                        <td className="px-4 py-3 border border-gray-200">
                          <div className="text-sm text-gray-700">
                            {location ? location.name : completion.locationName || 'N/A'}
                          </div>
                        </td>
                        <td className="px-4 py-3 border border-gray-200">
                          <div className="text-sm text-gray-700">
                            {completion.completedByName || completion.completedBy || 'N/A'}
                          </div>
                        </td>
                        <td className="px-4 py-3 border border-gray-200">
                          <div className="text-sm">
                            <span className="text-gray-700">
                              {status.completed} / {status.total}
                            </span>
                            {status.total > 0 && (
                              <span className="ml-2 text-gray-500">
                                ({Math.round((status.completed / status.total) * 100)}%)
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 border border-gray-200">
                          <div className="text-sm text-gray-700">
                            {assetName || ''}
                          </div>
                        </td>
                        <td className="px-4 py-3 border border-gray-200">
                          <div className="flex justify-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => handleViewCompletion(completion)}
                              className="text-purple-600 hover:text-purple-800 text-xs px-2 py-1"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            {completion.pdfUrl && (
                              <button
                                onClick={() => handleDownloadPDF(completion)}
                                className="text-green-600 hover:text-green-800 text-xs px-2 py-1"
                                title="Download PDF"
                              >
                                <Download className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {filteredCompletions.length} of {completions.length} completion{completions.length !== 1 ? 's' : ''}
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors text-sm font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Completion Details Right-Side Panel */}
      {selectedCompletion && (
        <>
          {/* Click-out overlay to close */}
          <div
            className="fixed inset-0 bg-black bg-opacity-20 z-[125]"
            onClick={handleCloseCompletionDetails}
          />
          <div className="fixed inset-y-0 right-0 w-[500px] bg-white shadow-2xl border-l border-gray-200 z-[130] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50 flex-shrink-0">
              <div>
                <h3 className="text-sm font-semibold text-gray-800">
                  Checklist Run Details
                </h3>
                <p className="text-xs text-gray-600 mt-0.5">
                  {formatDate(getDateFromValue(selectedCompletion.completedAt))}
                </p>
              </div>
              <button
                onClick={handleCloseCompletionDetails}
                className="text-gray-500 hover:text-gray-700 p-1"
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-3 space-y-2" style={{ minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
              {isLoadingCompletionDetails ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                  <p className="text-sm text-gray-500 mt-2">Loading details...</p>
                </div>
              ) : viewingChecklist && selectedCompletion ? (
                <>
                  {/* Checklist Completion Info */}
                  <div className="bg-gray-50 border border-gray-200 rounded p-2">
                    <div className="space-y-1 text-xs">
                      <div>
                        <span className="font-medium text-gray-700">Checklist:</span>{' '}
                        <span className="text-gray-900">{selectedCompletion.checklistName || checklist.name}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Location:</span>{' '}
                        <span className="text-gray-900">{selectedCompletion.locationName || (selectedCompletion.locationId ? locations[selectedCompletion.locationId]?.name : 'Unknown') || 'Unknown'}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Completed By:</span>{' '}
                        <span className="text-gray-900">{(selectedCompletion.completedByName || selectedCompletion.completedBy) || 'Unknown'}</span>
                      </div>
                      {(() => {
                        const assetId = selectedCompletion.assetId || selectedCompletion.variableValues?.assetId;
                        const assetName = selectedCompletion.assetName || (assetId ? assetNames[assetId] : '');
                        return assetName ? (
                          <div>
                            <span className="font-medium text-gray-700">Asset:</span>{' '}
                            <span className="text-gray-900">{assetName}</span>
                          </div>
                        ) : null;
                      })()}
                      {/* Time Tracking Information */}
                      {(() => {
                        const activeTime = selectedCompletion.activeTimeSeconds !== undefined ? Number(selectedCompletion.activeTimeSeconds) : null;
                        const totalTime = selectedCompletion.totalTimeSeconds !== undefined ? Number(selectedCompletion.totalTimeSeconds) : null;
                        const pauseCount = selectedCompletion.pauseCount !== undefined ? Number(selectedCompletion.pauseCount) : null;
                        
                        return (
                          <>
                            {activeTime !== null && (
                              <div>
                                <span className="font-medium text-gray-700">Active Time:</span>{' '}
                                <span className="text-gray-900">{formatTimeDisplay(activeTime)}</span>
                              </div>
                            )}
                            {totalTime !== null && (
                              <div>
                                <span className="font-medium text-gray-700">Total Time:</span>{' '}
                                <span className="text-gray-900">{formatTimeDisplay(totalTime)}</span>
                              </div>
                            )}
                            {pauseCount !== null && pauseCount > 0 && (
                              <div>
                                <span className="font-medium text-gray-700">Pauses:</span>{' '}
                                <span className="text-gray-900">{pauseCount}</span>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Variables */}
                  {selectedCompletion.variableValues && Object.keys(selectedCompletion.variableValues || {}).length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded p-2">
                      <h4 className="text-xs font-semibold text-gray-800 mb-1">Variables</h4>
                      <div className="space-y-1">
                        {Object.entries(selectedCompletion.variableValues || {}).map(([key, value]) => (
                          <div key={key} className="text-xs">
                            <span className="font-medium text-gray-700">{key}:</span>{' '}
                            <span className="text-gray-900">{String(value)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Checklist Items */}
                  <div>
                    <h4 className="text-xs font-semibold text-gray-800 mb-1.5">
                      Checklist Items ({
                        selectedCompletion.items
                          ? Object.values(selectedCompletion.items || {}).filter((v: any) => v).length
                          : 0
                      }/{viewingChecklist.items?.length || 0} completed)
                    </h4>
                    <div className="space-y-1 border border-gray-200 rounded p-2 max-h-[400px] overflow-y-auto">
                      {viewingChecklist.items?.map((item, index) => {
                        // Use items (mobile format)
                        const items = selectedCompletion.items || {};
                        const isCompleted = items[item.text] || false;
                        const photoData = selectedCompletion.photos?.[item.text];
                        // Normalize photos to array: handle both string (single photo) and string[] (multiple photos)
                        const photos = photoData 
                          ? (Array.isArray(photoData) ? photoData : [photoData])
                          : [];
                        const taskRefs = (selectedCompletion.itemTaskReferences as Record<string, string[]>)?.[item.text] || [];
                        return (
                          <div key={index} className="flex items-start gap-2 p-1.5 bg-gray-50 rounded border border-gray-200">
                            <div className="flex-1 min-w-0">
                              <div className="text-xs text-gray-900">
                                <span className="font-medium">{index + 1}.</span> {item.text}
                                {isCompleted ? (
                                  <span className="ml-2 text-green-700">✓</span>
                                ) : (
                                  <span className="ml-2 text-gray-400">•</span>
                                )}
                              </div>
                              {isCompleted && taskRefs.length > 0 && (
                                <div className="mt-1 text-xs text-blue-600 font-medium">
                                  ✓ Completed by creating task{taskRefs.length > 1 ? 's' : ''} ({taskRefs.length} task{taskRefs.length > 1 ? 's' : ''} created)
                                </div>
                              )}
                              {photos.length > 0 && (
                                <div className="mt-1 flex flex-wrap gap-2">
                                  {photos.map((photoUrl, photoIndex) => (
                                    <img
                                      key={photoIndex}
                                      src={photoUrl}
                                      alt={`${item.text} photo ${photoIndex + 1}`}
                                      className="max-w-full h-auto rounded border border-gray-300 max-h-32 cursor-pointer"
                                      onClick={() => window.open(photoUrl, '_blank')}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-sm">Unable to load completion details</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ChecklistHistory;

